import { request } from './api';

export type UploadPurpose = 'projectImage' | 'attachment';

export type UploadedFile = {
  /** A pending key. It only becomes permanent when a row references it. */
  key: string;
  filename: string;
  contentType: string;
  size: number;
};

/**
 * Upload a file straight to S3 and return the pending key plus the metadata the
 * caller needs to record against a row.
 *
 * The PUT deliberately does not go through `request()`: that helper sends
 * `credentials: 'include'` and forces a JSON content type, and both would break
 * the signature. Both `Content-Type` and `Content-Length` are inside the signed
 * URL, so the content type must be the exact string the server returned and the
 * body must be the raw File -- wrapping it in FormData would change the byte
 * count and fail. Any drift is a 403, not a warning.
 */
export async function uploadFile(file: File, purpose: UploadPurpose): Promise<UploadedFile> {
  // Browsers leave `type` empty for extensions they do not recognise. The server
  // signs whatever it echoes back, so a fallback here beats a 400.
  const requestedType = file.type || 'application/octet-stream';

  const { uploadUrl, key, contentType } = await request<{
    uploadUrl: string;
    key: string;
    contentType: string;
  }>('/api/uploads/presign', {
    method: 'POST',
    body: { purpose, contentType: requestedType, size: file.size },
  });

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });

  if (!response.ok) {
    throw new Error('The file could not be uploaded. Please try again.');
  }

  return { key, filename: file.name, contentType, size: file.size };
}
